import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import { fetchRequestCalendar } from "../../../api/t2tRequestsApi";

const SLA_COLOR = {
  breached: { bg: "#FEE2E2", border: "#EF4444", text: "#B91C1C" },
  at_risk:  { bg: "#FEF3C7", border: "#F59E0B", text: "#92400E" },
  on_track: { bg: "#D1FAE5", border: "#10B981", text: "#065F46" },
  no_sla:   { bg: "#F3F4F6", border: "#9CA3AF", text: "#374151" },
};

function slaColor(status) {
  return SLA_COLOR[status] || SLA_COLOR.no_sla;
}

export default function CalendarView({ onRequestClick }) {
  const calRef = useRef(null);

  const [events,       setEvents]       = useState([]);
  const [undated,      setUndated]      = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [currentRange, setCurrentRange] = useState(null);

  const loadEvents = useCallback(async (start, end) => {
    setLoading(true);
    const params = {};
    if (start) params.start = start;
    if (end)   params.end   = end;
    const r = await fetchRequestCalendar(params);
    setLoading(false);
    if (r.error) return;

    const rows = r.data?.data || [];
    const dated   = [];
    const undatedRows = [];

    rows.forEach(row => {
      const slaStatus = row.sla?.status || row.sla || "no_sla";
      const col = slaColor(slaStatus);
      if (row.due_at) {
        dated.push({
          id:              String(row.request_id),
          title:           `${row.ref_id} ${row.title}`,
          date:            row.due_at.slice(0, 10),
          backgroundColor: col.bg,
          borderColor:     col.border,
          textColor:       col.text,
          extendedProps:   { row },
        });
      } else {
        undatedRows.push({ ...row, slaStatus, col });
      }
    });

    setEvents(dated);
    setUndated(undatedRows);
  }, []);

  // Initial load — current month
  useEffect(() => {
    const now   = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
    const end   = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
    setCurrentRange({ start, end });
    loadEvents(start, end);
  }, [loadEvents]);

  const handleDatesSet = (info) => {
    const start = info.startStr.slice(0, 10);
    const end   = info.endStr.slice(0, 10);
    // Avoid re-fetch on mount (already fetched above)
    if (currentRange && (start !== currentRange.start || end !== currentRange.end)) {
      setCurrentRange({ start, end });
      loadEvents(start, end);
    }
  };

  const handleEventClick = ({ event }) => {
    const row = event.extendedProps?.row;
    if (row && onRequestClick) onRequestClick(row.request_id);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, overflow: "hidden" }}>
      {/* Toolbar */}
      <div
        style={{
          padding: "10px 16px",
          background: "#fff",
          borderBottom: "1px solid #E5E7EB",
          display: "flex",
          alignItems: "center",
          gap: 16,
          flexShrink: 0,
          flexWrap: "wrap",
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <i className="bi bi-calendar3 text-primary" />
          <span style={{ fontWeight: 600, fontSize: 13 }}>Calendar View</span>
        </div>

        {/* SLA legend */}
        <div className="d-flex align-items-center gap-3 ms-auto" style={{ fontSize: 11 }}>
          {[
            { key: "on_track", label: "On Track" },
            { key: "at_risk",  label: "At Risk"  },
            { key: "breached", label: "Breached" },
            { key: "no_sla",   label: "No SLA"   },
          ].map(({ key, label }) => (
            <span key={key} className="d-flex align-items-center gap-1">
              <span
                style={{
                  display: "inline-block", width: 10, height: 10, borderRadius: 2,
                  background: SLA_COLOR[key].bg, border: `1.5px solid ${SLA_COLOR[key].border}`,
                }}
              />
              <span style={{ color: "#6B7280" }}>{label}</span>
            </span>
          ))}
          {loading && (
            <span className="spinner-border spinner-border-sm text-secondary ms-2" style={{ width: 14, height: 14 }} />
          )}
        </div>
      </div>

      {/* Calendar */}
      <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: "12px 16px" }}>
        <FullCalendar
          ref={calRef}
          plugins={[dayGridPlugin, listPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left:   "prev,next today",
            center: "title",
            right:  "dayGridMonth,dayGridWeek,listWeek",
          }}
          buttonText={{
            today:       "Today",
            month:       "Month",
            week:        "Week",
            listWeek:    "List",
          }}
          events={events}
          datesSet={handleDatesSet}
          eventClick={handleEventClick}
          eventDisplay="block"
          dayMaxEvents={4}
          moreLinkContent={({ num }) => `+${num} more`}
          height="100%"
          eventDidMount={(info) => {
            info.el.title = info.event.title;
            info.el.style.cursor = "pointer";
            info.el.style.fontSize = "11px";
            info.el.style.borderRadius = "4px";
            info.el.style.padding = "1px 4px";
          }}
        />
      </div>

      {/* Undated requests row */}
      {undated.length > 0 && (
        <div
          style={{
            borderTop: "1px solid #E5E7EB",
            background: "#FAFAFA",
            padding: "10px 16px",
            flexShrink: 0,
          }}
        >
          <div style={{ fontSize: 11, color: "#9CA3AF", fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
            No Due Date ({undated.length})
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {undated.map(row => (
              <button
                key={row.request_id}
                onClick={() => onRequestClick && onRequestClick(row.request_id)}
                style={{
                  fontSize: 11, padding: "3px 8px", borderRadius: 4, cursor: "pointer",
                  background: row.col.bg, border: `1px solid ${row.col.border}`,
                  color: row.col.text, fontWeight: 500,
                }}
                title={`${row.title} · ${row.workflow_name}`}
              >
                {row.ref_id} {row.title.length > 24 ? row.title.slice(0, 24) + "…" : row.title}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

CalendarView.propTypes = {
  onRequestClick: PropTypes.func,
};
